import type { ApexOptions } from 'apexcharts';

import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';

import { useFormatText } from '@/hooks/useFormatText';
import { useSettingsStore } from '@/store/useSettingsStore';
import { ILessonChartItem } from '@/types/class';

interface ILessonCompletionChartProps {
  lessons: ILessonChartItem[];
}

const MAX_LABEL_LENGTH = 10;

const LessonCompletionChart: React.FC<ILessonCompletionChartProps> = ({
  lessons,
}) => {
  const direction = useSettingsStore((s) => s.settings.direction);
  const isRTL = direction === 'rtl';
  const { formatText } = useFormatText();

  const maxProgress = useMemo(() => {
    if (!lessons?.length) return 100;
    const maxVal = Math.max(...lessons.map((l) => l.average_progress ?? 0));
    if (maxVal <= 0) return 100;
    // Round up to nearest 25
    return Math.ceil(maxVal / 25) * 25;
  }, [lessons]);

  const series = useMemo(
    () => [
      {
        name: formatText('teacher-classes.details.completion'),
        data: lessons.map((l) => l.average_progress ?? 0),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lessons]
  );

  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'bar',
        height: 260,
        toolbar: { show: false },
        fontFamily: 'Aptos, sans-serif',
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '60%',
          borderRadius: 6,
          borderRadiusApplication: 'end',
          distributed: false,
          dataLabels: { position: 'top' },
        },
      },
      dataLabels: { enabled: false },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent'],
      },
      xaxis: {
        categories: lessons.map((l) => l.name),
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          rotate: 0,
          rotateAlways: false,
          hideOverlappingLabels: false,
          trim: true,
          maxHeight: 40,
          style: {
            colors: '#525866',
            fontSize: '12px',
            fontWeight: 400,
          },
          formatter: (value: string) => {
            if (value && value?.length > MAX_LABEL_LENGTH) {
              return `${value?.slice(0, MAX_LABEL_LENGTH)}...`;
            }
            return value;
          },
        },
        tooltip: {
          enabled: true,
        },
      },
      yaxis: {
        min: 0,
        max: maxProgress,
        tickAmount: 4,
        opposite: isRTL,
        labels: {
          style: {
            colors: '#525866',
            fontSize: '12px',
            fontWeight: 400,
          },
          formatter: (value: number) => value.toString(),
        },
      },
      fill: {
        opacity: 1,
        colors: ['#F97316'],
      },
      grid: {
        borderColor: '#E1E4EA',
        strokeDashArray: 0,
        position: 'back',
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: { top: 0, right: 0, bottom: 0, left: 0 },
      },
      tooltip: {
        enabled: true,
        theme: 'light',
        style: {
          fontSize: '12px',
          fontFamily: 'Aptos, sans-serif',
        },
        custom: ({ dataPointIndex }) => {
          const lesson = lessons[dataPointIndex];
          const completionLabel = formatText(
            'teacher-classes.details.completion'
          );

          if (isRTL) {
            return `
              <div style="background: white; border: 1px solid #f5f7fa; border-radius: 5px; box-shadow: 0 0 18px rgba(38,38,38,0.2); padding: 10px 11px; min-width: 120px; direction: rtl; text-align: right;">
                <div style="font-weight: 600; font-size: 12px; line-height: 20px; color: #F97316; margin-bottom: 8px; font-family: Aptos, sans-serif;">
                  ${lesson.name}
                </div>
                <div style="display: flex; justify-content: space-between; align-items: center; gap: 12px;">
                  <span style="font-weight: 600; font-size: 12px; color: #181b25; font-family: Aptos, sans-serif;">
                    ${lesson.average_progress ?? 0}%
                  </span>
                  <span style="font-weight: 400; font-size: 12px; color: #525866; font-family: Aptos, sans-serif;">
                    ${completionLabel}
                  </span>
                </div>
              </div>
            `;
          }

          return `
            <div style="background: white; border: 1px solid #f5f7fa; border-radius: 5px; box-shadow: 0 0 18px rgba(38,38,38,0.2); padding: 10px 11px; min-width: 120px;">
              <div style="font-weight: 600; font-size: 12px; line-height: 20px; color: #F97316; margin-bottom: 8px; font-family: Aptos, sans-serif;">
                ${lesson.name}
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center; gap: 12px;">
                <span style="font-weight: 400; font-size: 12px; color: #525866; font-family: Aptos, sans-serif;">
                  ${completionLabel}
                </span>
                <span style="font-weight: 600; font-size: 12px; color: #181b25; font-family: Aptos, sans-serif;">
                  ${lesson.average_progress ?? 0}%
                </span>
              </div>
            </div>
          `;
        },
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lessons, isRTL, maxProgress]
  );

  return (
    <div className="w-full">
      <ReactApexChart
        options={options}
        series={series}
        type="bar"
        height={200}
      />
    </div>
  );
};

export default LessonCompletionChart;
